import {
  getValueFromObject,
  isExistAndPositiveInteger,
  accusative
} from './common';
import { makeHostSum, normalizePath } from './url';
import { getItemSEOQuery, getSEOData } from '../../api/queries';
import CONFIG from '../../config';
import { getI18nForLang } from './getI18nForLang';

const rangeTranslation = (from, to, unit, currI18n) => {
  let result;
  if (isExistAndPositiveInteger(from) && isExistAndPositiveInteger(to)) {
    result = currI18n.t('search.search_title_service.from_and_to', {
      from,
      to,
      unit
    });
  } else if (isExistAndPositiveInteger(from)) {
    result = currI18n.t('search.search_title_service.from', {
      from,
      unit
    });
  } else if (isExistAndPositiveInteger(to)) {
    result = currI18n.t('search.search_title_service.to', {
      to,
      unit
    });
  }

  return result;
};

export const getClearAsPath = asPath =>
  asPath.split('?')[0].split('#')[0] || asPath;

export const getBasedSeoData = (asPath, req, withoutQueryParams = false) => {
  const newAsPath = normalizePath(asPath);
  const rawHostSum = req ? makeHostSum({ asPath: newAsPath, req }) : null;
  const hostSum = withoutQueryParams
    ? {
        ...rawHostSum,
        currentUrl: getClearAsPath(rawHostSum.currentUrl)
      }
    : rawHostSum;

  return { newAsPath, hostSum };
};

export const getSeoData = async (
  apolloClient,
  asPath = '/',
  req,
  withoutQueryParams = false
) => {
  if (
    !apolloClient ||
    (typeof window === 'undefined' && process.env.NEXT_PUBLIC_E2E === '1')
  ) {
    return {};
  }

  const { newAsPath, hostSum } = getBasedSeoData(
    asPath,
    req,
    withoutQueryParams
  );
  const isIncludeParams = asPath.includes('?');

  if (isIncludeParams) {
    return {
      title: null,
      description: null,
      h1: null,
      asPath: newAsPath,
      hostSum
    };
  }

  const {
    data: { seo }
  } = await apolloClient.query({
    query: getSEOData,
    variables: { path: newAsPath }
  });

  return {
    title: seo?.title ?? null,
    description: seo?.description ?? null,
    h1: seo?.h1 ?? null,
    asPath: newAsPath,
    hostSum
  };
};

export const getCustomSeoData = (customData, asPath = '/', req) => {
  const { newAsPath, hostSum } = getBasedSeoData(asPath, req);

  return {
    ...customData,
    asPath: newAsPath,
    hostSum
  };
};

export const getResidenceSeoData = (name, lng, asPath, req) => {
  const threadSafeI18n = getI18nForLang(lng || CONFIG.defaultLocale);

  const title = name
    ? threadSafeI18n.t('pages.residence.meta_title', {
        lng,
        residence_name: name
      })
    : null;

  const description = name
    ? threadSafeI18n.t('pages.residence.meta_description', {
        lng,
        residence_name: name
      })
    : null;

  return getCustomSeoData(
    {
      title,
      description,
      h1: name ?? null
    },
    asPath,
    req
  );
};

export const getAgencySeoData = (name, lng, asPath, req) => {
  const threadSafeI18n = getI18nForLang(lng || CONFIG.defaultLocale);

  const title = name
    ? threadSafeI18n.t('pages.agency.meta_title', {
        lng,
        agency_name: name
      })
    : null;

  const description = name
    ? threadSafeI18n.t('pages.agency.meta_description', {
        lng,
        agency_name: name
      })
    : null;

  return getCustomSeoData(
    {
      title,
      description,
      h1: name ?? null
    },
    asPath,
    req
  );
};

const generateSeoMetaTags = metaTags =>
  metaTags?.reduce(
    (result, { name, content }) => ({ ...result, [name]: content }),
    {}
  ) ?? {};

export const generateItemSeoObj = (item, newAsPath, hostSum) => {
  const seo = generateSeoMetaTags(item?.metaTags);

  return {
    title: seo?.title ? `${seo?.title} - bina.az` : null,
    description: seo?.description ?? null,
    asPath: newAsPath,
    hostSum
  };
};

export const getItemSeoData = async (apolloClient, asPath, req) => {
  if (
    !apolloClient ||
    (typeof window === 'undefined' && process.env.NEXT_PUBLIC_E2E === '1')
  ) {
    return {};
  }
  const id = asPath.match(/\d+/)?.[0];
  const newAsPath = normalizePath(asPath);
  const hostSum = makeHostSum({ asPath: newAsPath, req });

  const {
    data: { item }
  } = await apolloClient.query({ query: getItemSEOQuery, variables: { id } });

  return generateItemSeoObj(item, newAsPath, hostSum);
};

export const createSeoConfig = (seoData, fbAppId, currI18n) => {
  if (!seoData || Object.keys(seoData).length === 0) {
    return false;
  }

  const title = seoData.title || currI18n.t('default_title');
  const description = seoData.description
    ? seoData.description
    : currI18n.t('default_description');
  const { currentUrl, path } = seoData?.hostSum;

  return {
    title,
    description,
    openGraph: {
      url: path === '/' ? currentUrl : null
    },
    facebook: {
      appId: fbAppId
    }
  };
};

export const getInitialSeoData = (router, lng, seoData, fbAppId, currI18n) => {
  const path = router.state?.asPath || '/';
  const host = lng === 'ru' ? `${lng}.${CONFIG.host}` : CONFIG.host;

  const defaultHostSum = {
    currentUrl: `${CONFIG.protocol}://${host}`,
    protocol: CONFIG.protocol,
    host: CONFIG.host,
    path
  };

  const seo = seoData?.hostSum?.path
    ? seoData
    : { ...seoData, hostSum: defaultHostSum };

  return { seo, seoConfig: createSeoConfig(seo, fbAppId, currI18n) };
};

export class SearchItemsTitleService {
  constructor(data, filter, currI18n) {
    this.data = data;
    this.filter = filter;
    this.i18n = currI18n;
  }

  action = () => {
    if (typeof this.filter.leased === 'undefined') return null;
    const type = this.filter.leased ? 'rent' : 'buy';

    return this.i18n.t(`search.search_title_service.${type}`);
  };

  category = () => {
    if (!isExistAndPositiveInteger(this.filter.categoryId))
      return this.i18n.t('search.search_title_service.empty_category');
    return accusative(
      getValueFromObject(
        this.data.categories,
        this.filter.categoryId,
        'name'
      ).toLowerCase(),
      this.i18n.language
    );
  };

  paidDaily = () =>
    this.filter.paidDaily
      ? this.i18n.t(`search.search_title_service.paid_daily`)
      : null;

  rooms = () => {
    if (!this.filter.roomIds || this.filter.roomIds.length > 1) {
      return null;
    }
    return this.i18n.t('search.form.room_count', {
      postProcess: 'interval',
      count: this.filter.roomIds[0] !== '5+' ? this.filter.roomIds[0] : '5'
    });
  };

  city = () => getValueFromObject(this.data.cities, this.filter.cityId, 'name');

  area = () => {
    const units = this.i18n.t('search.search_title_service.units.metres');

    return rangeTranslation(
      this.filter.areaFrom,
      this.filter.areaTo,
      units,
      this.i18n
    );
  };

  price = () =>
    rangeTranslation(
      this.filter.priceFrom,
      this.filter.priceTo,
      'AZN',
      this.i18n
    );

  location = () => {
    const locationsNamesArray =
      this.filter.locationIds &&
      this.filter.locationIds.map(item =>
        getValueFromObject(this.data.locations, item, 'name')
      );

    if (!locationsNamesArray || locationsNamesArray.length > 1) {
      return null;
    }

    return locationsNamesArray[0];
  };

  getTitle = () => {
    const title = this.i18n
      .t('search.search_title_service.format', {
        action: this.action(),
        category: this.category(),
        paidDaily: this.paidDaily(),
        rooms: this.rooms(),
        city: this.city(),
        area: this.area(),
        price: this.price(),
        location: this.location()
      })
      .split()
      .join(' ')
      .replace(/\s+,/g, ',')
      .replace(/,+,*/g, ',')
      .replace(/,\s$/, '')
      .replace(/\s, /, '')
      .trim();
    return `${title.replace(/^./, title[0].toUpperCase())}`;
  };
}

export class SearchItemsH1Service extends SearchItemsTitleService {
  price = () =>
    (!(this.filter.locationIds && this.filter.locationIds.length > 1) &&
      rangeTranslation(
        this.filter.priceFrom,
        this.filter.priceTo,
        'AZN',
        this.i18n
      )) ||
    '';

  location = () => {
    const locationsNamesArray =
      this.filter.locationIds &&
      this.filter.locationIds.map(item =>
        getValueFromObject(this.data.locations, item, 'name', 'id')
      );

    return locationsNamesArray ? locationsNamesArray.join(', ') : null;
  };

  getH1 = () =>
    this.i18n
      .t('search.search_title_service.format', {
        action: this.action(),
        category: this.category(),
        paidDaily: this.paidDaily(),
        rooms: this.rooms(),
        city: this.city(),
        area: this.area(),
        price: this.price(),
        location: this.location()
      })
      .split()
      .join(' ')
      .replace(/\s+,/g, ',')
      .replace(/,+,*/g, ',')
      .replace(/,\s$/, '')
      .replace(/\s, /, '');
}
